import { useEffect, useState } from "react";
import { Loader2, Trash2, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui-kit/Card";
import { useAuth } from "@/auth/AuthContext";

export function MaintenanceCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Owner-only: gated on the immutable is_owner flag from the backend (the editable
  // display title is never consulted). Non-owners never see this card.
  const load = () => api.get("/maintenance/data-counts").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (user?.is_owner) load(); }, [user]);

  if (!user?.is_owner) return null;
  if (!data) return null;

  const phrase = data.confirm_phrase;
  const armed = confirm.trim() === phrase;

  const run = async () => {
    setRunning(true); setError(""); setResult(null);
    try {
      const r = await api.post("/maintenance/reset-operational-data", { confirm: confirm.trim() });
      setResult(r.data);
      setConfirm("");
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setRunning(false); }
  };

  return (
    <Card className="border-destructive/40" data-testid="maintenance-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-5 w-5" /> Clear operational data (keep firm setup)
        </CardTitle>
        <CardDescription>
          Owner-only. Permanently deletes all clients, matters, time, invoices, payments, costs and
          activity — for a clean-slate pilot. Your firm settings, branding, users, LawPay connection,
          email templates and backups are <strong>kept</strong>. Back up first.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3" data-testid="maintenance-operational">
            <p className="text-xs font-medium text-destructive">Will be cleared ({data.operational.length})</p>
            <ul className="mt-1.5 space-y-0.5 text-sm text-foreground">
              {data.operational.map((c) => (
                <li key={c} className="flex justify-between" data-testid={`maint-op-${c}`}>
                  <span className="font-mono text-xs">{c}</span>
                  <span className="nums text-muted-foreground">{data.counts[c]}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-border bg-secondary/30 p-3" data-testid="maintenance-preserved">
            <p className="text-xs font-medium text-status-paid">Preserved ({data.preserved.length})</p>
            <ul className="mt-1.5 space-y-0.5 text-sm text-foreground">
              {data.preserved.map((c) => (
                <li key={c} className="flex justify-between" data-testid={`maint-keep-${c}`}>
                  <span className="font-mono text-xs">{c}</span>
                  <span className="nums text-muted-foreground">{data.counts[c]}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="maint-confirm">Type <span className="font-mono font-semibold text-destructive">{phrase}</span> to confirm</Label>
          <Input id="maint-confirm" data-testid="maintenance-confirm-input" value={confirm}
            onChange={(e) => setConfirm(e.target.value)} placeholder={phrase} autoComplete="off" />
        </div>

        <Button onClick={run} disabled={!armed || running} data-testid="maintenance-run-button"
          className="bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/40">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          Clear operational data
        </Button>

        {error && <p className="text-sm text-destructive" data-testid="maintenance-error">{error}</p>}
        {result && (
          <div className="rounded-lg border border-status-paid/30 bg-status-paid/5 p-3 text-sm" data-testid="maintenance-result">
            <p className="font-medium text-status-paid">Cleared {result.total_deleted} document(s).</p>
            {result.backup && (
              <p className="mt-1 text-xs text-muted-foreground" data-testid="maintenance-backup-info">
                Safety backup created first — restore point <span className="font-mono">{result.backup.backup_id}</span>
                {result.backup.created_at ? ` at ${new Date(result.backup.created_at).toLocaleString()}` : ""}.
              </p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              Logged as <span className="font-mono">operational_data_reset</span> by {result.actor} at {new Date(result.at).toLocaleString()}.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
